import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { setSensors } from "../features/shipBuilderSlice";
import { setPowerPlant, setFuelTanks } from "../features/shipBuilderSlice";
import { setSectionSensors } from "../features/breakawayHullSlice";
import { calculatePowerPlantRequirements } from "../../utils/calculations/powerCalculations";
import { calculateFuelTankRequirements } from "../../utils/calculations/fuelCalculations";

/**
 * Middleware to automatically update power plant and fuel tanks when sensors are changed
 * This ensures the ship always has sufficient power and fuel capacity for its sensor systems
 */
const sensorPowerPlantMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    const result = next(action);

    // Process both setSensors and setSectionSensors actions
    if (setSensors.match(action) || setSectionSensors.match(action)) {
      const state = store.getState().shipBuilder;
      const breakawayState = store.getState().breakawayHull;
      const { sensors, shipSize, techLevel, drives, powerPlant } = state;

      // Calculate sensor power requirement from both regular and breakaway sensors
      let sensorPowerRequirement = sensors ? sensors.power : 0;
      
      // Add breakaway hull sensor power if breakaway hulls are active
      if (breakawayState?.isBreakaway && breakawayState.sections) {
        const breakawaySensorPower = breakawayState.sections.reduce((total, section) => {
          return total + (section.sensors ? section.sensors.power : 0);
        }, 0);
        sensorPowerRequirement += breakawaySensorPower;
      }

      // Get current power plant output
      const currentPowerOutput = powerPlant ? powerPlant.power : 0;

      // Use shipStats.powerRequired for total ship power, fallback to basic systems
      const totalPowerRequired = state.shipStats?.powerRequired || (shipSize * 0.2);

      // Check if current power plant can handle the new power requirements
      if (currentPowerOutput < totalPowerRequired) {
        console.log(`Sensor power requirement: ${sensorPowerRequirement}, Total required: ${totalPowerRequired}, Current output: ${currentPowerOutput}`);
        
        // Calculate new power plant requirements
        const powerPlantResult = calculatePowerPlantRequirements({
          powerRequired: totalPowerRequired,
          shipSize,
          techLevel,
        });

        if (powerPlantResult.success && powerPlantResult.data) {
          console.log("Updating power plant due to sensor change:", powerPlantResult.data);
          
          // Update power plant
          store.dispatch(setPowerPlant(powerPlantResult.data));

          // Update fuel tanks based on new power plant
          const fuelTankResult = calculateFuelTankRequirements({
            powerPlant: powerPlantResult.data,
            drives,
            shipSize,
          });

          if (fuelTankResult.success && fuelTankResult.data) {
            console.log("Updating fuel tanks due to power plant change:", fuelTankResult.data.total);
            store.dispatch(setFuelTanks(fuelTankResult.data.total));
          }
        }
      }
    }

    return result;
  };

export default sensorPowerPlantMiddleware;
